import { Component, inject } from '@angular/core'
import { TextInputComponent } from '@ui/Components/Form/TextInput/TextInput.Component'
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import {
	FormValidationComponent,
	ValidationMessages,
} from '@ui/Components/Form/FormValidation.Component'
import HttpError, { FormErrors } from '@ui/HttpError'
import { CustomValidators } from '@ui/Validators/CustomValidators'
import { RaisedButtonComponent } from '@ui/Components/Form/Button/RaisedButton.Component'
import User from '@client/User'

import { SystemConfigService } from '@client/Services/SystemConfigService'
import { RecaptchaService } from '@ui/Services/RecaptchaService'
import { UsersService } from '@client/Services/UsersService'
import { PermissionsService } from '@client/Services/PermissionsService'
import { Router } from '@angular/router'
import { FlatButtonComponent } from '@ui/Components/Form/Button/FlatButton.Component'

@Component({
	selector: 'index-component',
	templateUrl: './Index.Component.html',
	imports: [
		TextInputComponent,
		FormsModule,
		ReactiveFormsModule,
		RaisedButtonComponent,
		FlatButtonComponent,
	],
})
export class IndexComponent extends FormValidationComponent {
	public formErrors: FormErrors = {
		name: '',
		email: '',
	}

	public submitting: boolean = false

	public registered: boolean = false

	public successMessage: string = ''

	protected validationMessages: ValidationMessages = {
		name: {
			required: 'Name is required',
		},
		email: {
			required: 'Email is required',
			invalidEmailAddress: 'Email address must be valid',
		},
	}

	protected readonly permissionsService: PermissionsService

	private readonly userService: UsersService

	private readonly systemConfigService: SystemConfigService

	private readonly recaptchaService = inject(RecaptchaService)

	private readonly router: Router

	constructor() {
		super()

		this.userService = inject(UsersService)
		this.systemConfigService = inject(SystemConfigService)
		this.recaptchaService = inject(RecaptchaService)
		this.permissionsService = inject(PermissionsService)
		this.router = inject(Router)
	}

	public goToAccount = async (): Promise<void> => {
		await this.router.navigateByUrl('/my-account')
	}

	public register = async (): Promise<void> => {
		this.validateForm()

		if (this.form.valid) {
			this.submitting = true

			try {
				const token = await this.recaptchaService.validate(
					this.systemConfigService.config.recaptcha,
				)

				const user = await this.userService.create(
					new User(undefined, this.form.value.name, this.form.value.email),
					token,
				)

				this.registered = true

				this.successMessage = `${user.name} has been successfully registered!`
			} catch (error) {
				if (error instanceof HttpError) {
					if (error.isUnprocessableEntity()) {
						this.formErrors = error.handleFormErrors(this.formErrors, 'user')

						this.markInvalidFields()
					}
				} else {
					throw error
				}
			}

			this.submitting = false
		}
	}

	protected buildForm = (): void => {
		this.form = this.formBuilder.group({
			name: ['', [Validators.required]],
			email: ['', [Validators.required, CustomValidators.emailValidator]],
		})
	}
}
